import { Component } from '@theme/component';
import { eventBus, VariantSelectedEvent, VariantUpdatedEvent, ThemeEvents } from '@theme/events';
import { CartActions } from '@theme/cart--state--actions';
import { morph, parseSection } from '@theme/morph';

/**
 * Product Context Provider — orchestrates variant selection via Section Rendering API.
 *
 * Wraps all product UI children (price, variant picker, buy buttons, add-to-cart).
 * On variant change, fetches fresh section HTML with ?variant=ID and morphs its
 * subtree. Liquid re-renders everything with the correct
 * `product.selected_or_first_available_variant`.
 *
 * @attr {string} data-section-id - Section ID for Section Rendering API
 * @attr {string} data-product-url - Product URL (e.g. /products/my-product)
 * @attr {string} data-selected-variant - Currently selected variant ID
 * @attr {string} data-product-id - Product ID
 * @attr {string} data-product-title - Product title
 * @attr {string} data-product-image - Product featured image URL
 *
 * @extends {Component}
 */
class ActionProductContext extends Component {
	/** @type {Object[]} Parsed product variants */
	#variants = [];

	/** @type {AbortController|null} Cancels in-flight fetch on rapid selection */
	#fetchController = null;

	/** @type {'idle'|'ready'|'notify'} UI state for ATC coordination */
	#uiState = 'idle';

	/** @type {{ value: string, stockLabel: string, variantId: string|number } | null} */
	#lastSelectedSize = null;

	/** @type {string|number|null} Variant id chosen while unavailable — survives morph re-application */
	#notifyVariantId = null;

	/** @type {(() => void)[]} Event bus unsubscribers */
	#unsubscribes = [];

	connectedCallback() {
		super.connectedCallback();
		this.#parseVariants();
		this.addEventListener(ThemeEvents.pdpSizeChosen, this.#handleSizeChosen);
		this.addEventListener(ThemeEvents.pdpSizeRequired, this.#handleSizeRequired);
		this.#unsubscribes.push(
			eventBus.on(CartActions.itemAdded, this.#handleCartItemAdded)
		);
		this.#setUIState('idle');
		this.#markPristine();
		if (!this.#seedFromUrlVariant()) {
			this.#autoSelectSingleVariant();
		}
	}

	/**
	 * Mark the cart-action-add as pristine so the ATC shows "Add to cart"
	 * enabled until the user interacts. Clicking (or picking a size) removes
	 * the attribute — after that the normal idle/ready/notify state machine
	 * takes over. Hover-based disabled feedback is handled purely via CSS.
	 */
	#markPristine() {
		const atc = this.querySelector('cart-action-add');
		if (!atc) return;
		atc.setAttribute('data-pristine', '');
		atc.addEventListener(
			'click',
			() => atc.removeAttribute('data-pristine'),
			{ once: true, capture: true }
		);
	}

	/**
	 * Seed the ATC state from the URL's ?variant= parameter (deep links from
	 * wishlist, shared links, etc.). The server already rendered this variant
	 * (price, data-selected-variant), so like #autoSelectSingleVariant this
	 * only drives UI state — no Section Rendering fetch. The variant picker
	 * fills its own trigger UI from `data-preselected-value` (dropdown style)
	 * or its pre-checked radios (buttons style).
	 * @returns {boolean} Whether a variant was seeded from the URL.
	 */
	#seedFromUrlVariant() {
		const param = new URLSearchParams(window.location.search).get('variant');
		if (!param) return false;

		const variant = this.#variants.find((v) => String(v.id) === param);
		if (!variant) return false;

		const row = /** @type {HTMLElement|null} */ (
			this.querySelector(`.product-variant-picker__row[data-variant-id="${variant.id}"]`)
		);
		const stockLabel = row?.dataset.stockLabel ?? '';

		if (variant.available === false) {
			this.#lastSelectedSize = null;
			this.#setUIState('notify', { variantId: variant.id });
		} else {
			this.#lastSelectedSize = { value: variant.option1 ?? '', stockLabel, variantId: variant.id };
			this.#setUIState('ready', { variantId: variant.id });
		}
		return true;
	}

	/**
	 * Seed the ATC state for a single-variant product. The picker renders
	 * nothing for it, so the lone variant comes from the already-parsed JSON.
	 *
	 * Drives the same state a real size pick would, minus the Section Rendering
	 * fetch — the server already rendered this variant.
	 */
	#autoSelectSingleVariant() {
		if (!this.#isSingleVariant()) return;

		const variant = this.#variants[0];
		const variantId = variant.id;

		if (variant.available === false) {
			this.#lastSelectedSize = null;
			this.#setUIState('notify', { variantId });
		} else {
			this.#lastSelectedSize = { value: variant.option1 ?? '', stockLabel: '', variantId };
			this.#setUIState('ready', { variantId });
		}
	}

	/**
	 * @returns {boolean}
	 */
	#isSingleVariant() {
		return this.#variants.length === 1;
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#fetchController?.abort();
		this.#fetchController = null;
		this.removeEventListener(ThemeEvents.pdpSizeChosen, this.#handleSizeChosen);
		this.removeEventListener(ThemeEvents.pdpSizeRequired, this.#handleSizeRequired);
		for (const unsub of this.#unsubscribes) unsub();
		this.#unsubscribes = [];
	}

	/**
	 * Re-parse variants after Section Rendering API morph.
	 * Re-apply the current UI state because morph replaces the ATC element
	 * (losing its data-atc-state attribute) and the variant picker (losing
	 * the user's selection — server always renders the placeholder).
	 */
	updatedCallback() {
		super.updatedCallback();
		this.#parseVariants();
		this.#setUIState(this.#uiState);

		if (this.#uiState === 'ready' && this.#lastSelectedSize) {
			const picker = this.querySelector('variant-picker-dropdown');
			picker?.setSelected?.(this.#lastSelectedSize.value, this.#lastSelectedSize.stockLabel);
		}
	}

	/**
	 * Coordinate the PDP UI state. Sets `data-atc-state` on the cart-action-add
	 * child so it can swap text + click behavior via CSS and JS.
	 * @param {'idle'|'ready'|'notify'} state
	 * @param {{ variantId?: string|number }} [context]
	 */
	#setUIState(state, context = {}) {
		this.#uiState = state;
		const atc = this.querySelector('cart-action-add');
		if (!atc) return;

		atc.setAttribute('data-atc-state', state);

		// Once the state transitions away from the initial idle seed, the
		// button is no longer pristine — either the user interacted or a
		// size was picked.
		if (state !== 'idle') atc.removeAttribute('data-pristine');

		if (state === 'notify') {
			// Remember the unavailable variant so this state survives a morph.
			// updatedCallback / #fetchAndMorph re-call #setUIState(this.#uiState)
			// WITHOUT context, and the server re-renders the ATC pointing at the
			// first available variant with no data-unavailable. Re-applying both
			// here keeps the picked sold-out variant non-addable after morph.
			const variantId = context.variantId ?? this.#notifyVariantId;
			if (variantId != null) {
				this.#notifyVariantId = variantId;
				// Point the ATC at the unavailable variant so its BIS request uses the right id
				atc.setAttribute('data-selected-variant', String(variantId));
				atc.setAttribute('data-unavailable', '');
			}
		} else {
			this.#notifyVariantId = null;
			if (state === 'ready') {
				atc.removeAttribute('data-unavailable');
				// Point the ATC at the chosen variant synchronously. Until the
				// Section Rendering morph lands, the attribute still holds the
				// previous variant (notify's sold-out id, or the server-rendered
				// first-available) — a fast click in that window would add or
				// notify the wrong variant.
				const variantId = context.variantId ?? this.#lastSelectedSize?.variantId;
				if (variantId != null) {
					atc.setAttribute('data-selected-variant', String(variantId));
				}
			}
		}
	}

	/**
	 * Handle a size pick from the variant picker dropdown.
	 * @param {CustomEvent} event
	 */
	#handleSizeChosen = (event) => {
		const data = event.detail?.data;
		if (!data) return;
		const { variantId, available, value, stockLabel } = data;

		if (available) {
			this.#lastSelectedSize = { value, stockLabel: stockLabel ?? '', variantId };
			this.#setUIState('ready', { variantId });
			// fetch+morph is triggered separately by the native radio change event
		} else {
			this.#lastSelectedSize = null;
			this.#setUIState('notify', { variantId });
		}
	};

	#handleSizeRequired = () => {
		const picker = this.querySelector('variant-picker-dropdown');
		picker?.open?.();
	};

	/**
	 * Reset UI state when the current product's variant is successfully added to cart.
	 */
	#handleCartItemAdded = (event) => {
		const eventProductId = event.detail?.data?.productId;
		const myProductId = this.dataset.productId;
		if (!myProductId || !eventProductId) return;
		if (String(eventProductId) !== String(myProductId)) return;

		// Single-variant products have nothing to re-pick — keep the
		// selection and ready state so the customer can add again immediately.
		if (this.#isSingleVariant()) return;

		this.#lastSelectedSize = null;
		this.#setUIState('idle');
		// Re-apply the pristine marker so the button returns to its initial
		// "Add to cart" enabled state (with hover-to-disabled feedback) —
		// same UX as a fresh PDP load.
		this.#markPristine();
		const picker = this.querySelector('variant-picker-dropdown');
		picker?.reset?.();
	};

	/**
	 * Parse the embedded variant JSON from the script tag.
	 */
	#parseVariants() {
		const script = this.querySelector('script[data-product-variants]');
		if (!script) return;

		try {
			this.#variants = JSON.parse(script.textContent);
		} catch {
			console.error('ActionProductContext: Failed to parse variant JSON');
		}
	}

	/**
	 * Handle option change — bound via on:change="/handleOptionChange" in Liquid.
	 * Catches bubbled change events from radio buttons in the variant picker.
	 * @param {Event} event
	 */
	handleOptionChange(event) {
		const options = this.#getSelectedOptions();
		if (!options.length) return;

		const variant = this.#findVariantByOptions(options);
		if (!variant) return;

		// Immediate updates (before network)
		this.#updateUrl(variant.id);
		this.#updateFormSelect(variant.id);
		this.dataset.selectedVariant = String(variant.id);

		// Notify other components
		eventBus.emit(new VariantSelectedEvent({
			resource: { id: variant.id },
		}));

		// Async: fetch + morph
		this.#fetchAndMorph(variant.id);
	}

	/**
	 * Collect selected option values from fieldsets in order.
	 * Relies on data-option-index on each fieldset.
	 * @returns {string[]} Array of selected values, indexed by option position
	 */
	#getSelectedOptions() {
		const options = [];
		const fieldsets = this.querySelectorAll('fieldset[data-option-index]');

		for (const fieldset of fieldsets) {
			const checked = fieldset.querySelector('input[type="radio"]:checked');
			if (checked) {
				const index = parseInt(fieldset.dataset.optionIndex, 10);
				options[index] = checked.value;
			}
		}

		return options;
	}

	/**
	 * Find a variant matching all selected options.
	 * Matches against variant.option1, option2, option3.
	 * @param {string[]} options
	 * @returns {Object|undefined}
	 */
	#findVariantByOptions(options) {
		return this.#variants.find((variant) => {
			for (let i = 0; i < options.length; i++) {
				if (options[i] === undefined) continue;
				const key = `option${i + 1}`;
				if (variant[key] !== options[i]) return false;
			}
			return true;
		});
	}

	/**
	 * Update URL with ?variant=ID for deep linking and back button support.
	 * @param {string|number} variantId
	 */
	#updateUrl(variantId) {
		const url = new URL(window.location.href);
		url.searchParams.set('variant', String(variantId));
		history.replaceState(null, '', url.toString());
	}

	/**
	 * Update the hidden form <select name="id"> so the product form submits the correct variant.
	 * @param {string|number} variantId
	 */
	#updateFormSelect(variantId) {
		const select = this.querySelector('select[name="id"]');
		if (select) {
			select.value = String(variantId);
		}
	}

	/**
	 * Fetch fresh section HTML with the selected variant and morph this element's subtree.
	 * @param {string|number} variantId
	 */
	async #fetchAndMorph(variantId) {
		// Cancel any in-flight fetch
		this.#fetchController?.abort();
		this.#fetchController = new AbortController();
		const { signal } = this.#fetchController;

		const sectionId = this.dataset.sectionId;
		const productUrl = this.dataset.productUrl;

		if (!sectionId || !productUrl) return;

		const url = `${productUrl}?variant=${variantId}&sections=${sectionId}`;

		try {
			const response = await fetch(url, { signal });
			if (!response.ok) throw new Error(`Section fetch failed (${response.status})`);

			const sections = await response.json();
			const html = sections[sectionId];
			if (!html) return;

			// Find this element's counterpart in the response HTML
			const newContext = parseSection(html, `#${CSS.escape(this.id)}`);
			if (!newContext) return;

			// Morph this element's children (childrenOnly: true is the default)
			morph(this, newContext);

			// Post-morph restoration: updatedCallback doesn't fire reliably
			// (morph's onAfterUpdate hook receives the detached parsed node, not the
			// live element, so `instanceof Component` is false). Re-run the same
			// work manually here to keep the UI state consistent after the server
			// re-renders the subtree.
			this.#parseVariants();
			this.#setUIState(this.#uiState);
			if (this.#uiState === 'ready' && this.#lastSelectedSize) {
				const picker = this.querySelector('variant-picker-dropdown');
				picker?.setSelected?.(this.#lastSelectedSize.value, this.#lastSelectedSize.stockLabel);
			}

			// Notify components outside this context
			eventBus.emit(new VariantUpdatedEvent({
				resource: { id: variantId },
				sourceId: this.id,
				data: {
					html,
					productId: this.dataset.productId,
				},
			}));
		} catch (error) {
			if (error.name === 'AbortError') return; // Expected on rapid selection
			console.error('ActionProductContext: Fetch failed', error);
		}
	}
}

if (!customElements.get('action-product-context')) {
	customElements.define('action-product-context', ActionProductContext);
}
